<template>
  <div>
    <SubmitalNavbar />

    <SubmitalHeader :submital="submital" page="submital" />

    <ContentView>
      <b-row class="mt-3">
        <b-col lg="6">
          <div class="section-title">Resumen:</div>
          <b-row align-h="center" class="acceso-info mb-3">
            <b-col cols="11" offset="1">
              <SubmitalSummary :ready="ready" />
            </b-col>
          </b-row>
        </b-col>
        <b-col lg="5" offset-lg="1">
          <!-- Quotes -->
          <b-row v-if="submital.id" align-h="between" class="px-3">
            <div class='section-title'>Cotización:</div>
            <div v-if="configurable">
              <div id="generate-button-info" class="clickable text-info d-inline ml-3">
                <font-awesome-icon :icon="['far', 'question-circle']" fixed-width />
              </div>
              <b-popover target="generate-button-info" placement="bottom" triggers="hover click">
                <span class="text-info">Genera una nueva cotización basada en el cliente, proyecto y motor seleccionado si este existe</span>
              </b-popover>
              <b-button size="sm" variant="outline-success" @click="showConfirmQuoteModal = true">
                Generar
              </b-button>
            </div>
          </b-row>
          <b-row v-if="quotes.length > 0" class="overflow-auto" style="height: 120px">
            <b-col>
              <b-list-group class="w-100 fs-13">
                <b-list-group-item v-for="quote in sortedQuotes" :key="quote.id" :to="`/app/prod/cotizador/${quote.id}`" class="py-2">
                  <span class="font-weight-bold">{{ quote.quote_id }}:</span><span class="pl-2">{{ quote.title }}</span>
                </b-list-group-item>
              </b-list-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col lg="6">
          <div class="section-title">Configurador:</div>
          <nav>
            <ul class="border-top" style="list-style: none; padding: 0">
              <SubmitalShowStepLink
                v-for="step in configuratorSteps"
                :step="step"
                :key="step.en"
                @step-link-clicked="goToConfiguratorStep"
              />
            </ul>
          </nav>
        </b-col>
        <b-col lg="5" offset-lg="1" class="font-family: 'Gill Sans', sans-serif">
          <div v-if="submital.id && ['Access', 'Puerta', 'ControlAcceso'].includes(submitalCategory)" class="mb-5">
            <div class="section-title">Reportes PDF:</div>
            <b-row v-if="submital.id" align-h="center" align-v="center">
              <b-col cols="6">
                <b-link class="my-button w-100" @click="downloadSubmital">
                  <font-awesome-icon :icon="['far', 'file-pdf']" size="lg" fixed-width />
                  <span>Submital</span>
                </b-link>
              </b-col>
              <b-col cols="4">
                <span v-if="ready && submital.progress == 100" class="text-success">Listo</span>
                <span v-else class="text-danger">Pendiente</span>
              </b-col>
              <b-col cols="2">
                <div class="email-toggle" @click="submitalEmailInputShow = !submitalEmailInputShow">
                  <font-awesome-icon :icon="['far', 'envelope']" size="lg" fixed-width />
                </div>
              </b-col>
            </b-row>
            <transition name="email-animation">
              <ValidationObserver v-if="submitalEmailInputShow" ref="email-section" v-slot="{invalid}">
                <b-row align-h="end">
                  <b-col>
                    <ValidationProvider rules="required|email" name="Correo" v-slot="{ valid, errors }">
                      <b-input-group class="mt-3">
                        <b-input type="email" v-model="submitalEmailText" size="sm" :state="errors[0] ? false : (valid ? true : null)" placeholder="correo"/>
                        <b-input-group-append>
                          <b-button :disabled="invalid" size="sm" variant="primary" @click="emailSubmital">
                            Enviar
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>
                    </ValidationProvider>
                  </b-col>
                </b-row>
              </ValidationObserver>
            </transition>
          </div>
          <div>
            <div class="section-title">Alarmas y Avisos:</div>
            <b-row v-if="ready && submital.id" class="alarms-list" align-h="center">
              <b-col cols="11" class="pr-0">
                <!-- TODO: don't forget lead alarm! -->
                <b-row v-for="warning in submital.warnings" :key="warning" class="text-warning">
                  <p class="mb-1">
                    <font-awesome-icon :icon="['far', 'exclamation-circle']" fixed-width />
                    <span>{{ warning }}</span>
                  </p>
                </b-row>
                <div v-for="(stepData, stepName) in submital.alarm_data" :key="stepName">
                  <div v-for="(alarmObj, index) in stepData.alarms" :key="index">
                    <b-row>
                      <b-link class="text-danger" @click="goToConfiguratorStep(stepName)">
                        <font-awesome-icon :icon="['far', 'ban']" fixed-width />
                        <span>{{ Object.values(alarmObj)[0] }}</span>
                      </b-link>
                    </b-row>
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </ContentView>

    <!-- <ConfiguratorModal v-if="submital" :submital="submital" /> -->

    <!-- Confirm Modal -->
    <ConfirmModal v-if="showConfirmQuoteModal" :required=true @close="showConfirmQuoteModal = false" @onConfirm="createQuote">
      <template #header>
        <h5 class="font-weight-bold text-dark mb-0">Crear cotización</h5>
      </template>
      <template #body>
        <div class="text-center py-2">
          <div class="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style="width: 64px; height: 64px; background-color: #eff6ff;">
            <i class="fas fa-info-circle fa-2x text-info"></i>
          </div>
          <p class="text-secondary mb-2">
            Se creará una nueva cotización para el cliente y proyecto de este submital
            <span v-if="configurable.motor_id">, con los artículos del motor seleccionado</span>
          </p>
          <p class="font-weight-bold text-dark mb-0">¿Desea continuar?</p>
        </div>
      </template>
    </ConfirmModal>

    <ToastAlert :show="alertShow" :variant="alertVariant" @close="alertShow = false">
      {{ alertMessage }}
    </ToastAlert>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { addDays } from 'date-fns'
import { ValidationObserver, ValidationProvider } from "vee-validate"

import Button from '../../shared/Button'
import ContentView from '../../shared/ContentView'
import ToastAlert from '../../shared/ToastAlert'
import SubmitalHeader from './SubmitalHeader'
import SubmitalShowStepLink from './SubmitalShowStepLink'
import SubmitalNavbar from './SubmitalNavbar'
import SubmitalSummary from './SubmitalSummary'
import ConfirmModal from '../../shared/ConfirmModal'
// import ConfiguratorModal from '../configurators/ConfiguratorModal'

export default {
  name: 'SubmitalShow',
  components: {
    Button,
    ContentView,
    ToastAlert,
    SubmitalHeader,
    SubmitalShowStepLink,
    // ConfiguratorModal,
    ValidationObserver,
    ValidationProvider,
    SubmitalNavbar,
    SubmitalSummary,
    ConfirmModal
  },
  data () {
    return {
      id: Number(this.$route.params.id),
      quotes: [],
      submitalEmailInputShow: false,
      submitalEmailText: '',
      ready: false,
      processing: false,
      alertShow: false,
      alertVariant: null,
      alertMessage: '',
      showConfirmQuoteModal: false
    }
  },
  computed: {
    ...mapState ({
      submital: state => state.configurator.submital,
      configurable: state => state.configurator.configurable
    }),
    ...mapGetters ([
      'configuratorSteps',
      'isCorredizo',
      'isAbatible',
      'isPuertaAutomatica',
      'isControlAcceso',
      'submitalCategory',
    ]),
    sortedQuotes () {
      return this.quotes.sort((a,b) => new Date(b.created_at) - new Date(a.created_at))
    }
  },
  mounted () {
    this.$store.commit('setCurrentStep', null)
    this.fetchSubmital()
  },
  methods: {
    fetchSubmital () {
      this.ready = false
      this.$http.get(`/api/production/submitals/${this.id}`)
        .then((res) => {
          const { configurable, quotes, ...submital } = res.data
          this.$store.commit('setConfigurable', configurable)
          this.$store.commit('setSubmital', submital)
          this.quotes = quotes
          this.ready = true
        })
        .catch(e => {
          // console.log('error',e)
          this.$store.commit('setConfigurable', {})
          this.$store.commit('setSubmital', {})
        })
    },
    goToConfiguratorStep (step) {
      if (!this.submital.id || step.en == 'not found') return
      let stepName = step
      if (typeof step === 'object' && step !== null) {
        this.$store.commit('setCurrentStep', step.en)
        stepName = step.en
      } else {
        this.$store.commit('setCurrentStep', step)
      }
      // new version!
      this.$router.push({ name: 'ConfiguratorStepLayout', params: { id: this.submital.id, step: stepName } })
      // TODO: old version: Modal, remove after testing
      // this.$bvModal.show('configurator-modal')
    },
    createQuote () {
      if (this.processing) { return }
      this.processing = true

      let businessUnit = 'DI'
      if (this.isCorredizo || this.isAbatible) {
        businessUnit = 'HZ'
      } else if (this.isPuertaAutomatica) {
        businessUnit = 'PA'
      } else if (this.isControlAcceso) {
        businessUnit = 'CA'
      }
      const motorId = this.configurable ? this.configurable.motor_id : null

      this.$http.post('/api/production/quotes', {
          quote: {
            user_id: this.submital.salesperson_id,
            client_id: this.submital.client_id,
            project_id: this.submital.project_id,
            submital_id: this.submital.id,
            business_unit: businessUnit,
            valid_until: addDays(new Date, 15),
            usd_crc_rate: this.$store.state.settings.usd_crc_rate,
            notes: `Generada desde submital ${this.submital.submital_no}`
          },
          motor_id: motorId
        })
        .then((res) => {
          // this.$router.push(`/app/prod/cotizador/${res.data.id}`)
          this.quotes.unshift(res.data)
          this.displayAlert({ message: `Cotización ${res.data.quote_id} se ha creado con éxito`, variant: 'success' })
          this.showConfirmQuoteModal = false
          // this.$http.get(`/api/production/quotes/${res.data.id}/generate`)
        })
        .catch((e) => {
          // console.log(e)
          this.displayAlert({ message: 'No funcionó', variant: 'danger' })
        })
        .finally(() => {
          this.alertShow = true
          this.processing = false
        })
    },
    downloadSubmital () {
      if (this.processing) { return }
      this.processing = true
      this.$http.get(`/api/production/submitals/${this.id}/download`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf'
        },
        responseType: 'blob',
        params: {
          report_type: 'submital'
        }
      })
        .then((res) => {
          const blob = new Blob([res.data], { type: 'application/pdf' })
          // Open in browser window
          // const fileUrl = window.URL.createObjectURL(blob)
          // window.open(fileUrl)

          // Download link
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = `Submital #${this.submital.submital_no}.pdf`
          link.click()
        })
        .catch(e => console.log(e))
        .finally(this.processing = false)
    },
    emailSubmital () {
      if (this.processing) return
      this.processing = true
      this.$http.get(`/api/production/submitals/${this.id}/email`, {
        params: {
          report_type: 'submital',
          email: this.submitalEmailText
        }
      })
        .then((res) => {
          console.log(res.data)
          this.submitalEmailInputShow = false
          this.displayAlert({ message: `El reporte ha sido enviado a el correo ${this.submitalEmailText}`, variant: 'success' })
          this.submitalEmailText = ''
        })
        .catch(e => {
          // console.log(e)
          this.displayAlert({ message: 'El correo no se envió, intente de nuevo', variant: 'danger' })
        })
        .finally(this.processing = false)
    },
    displayAlert ({ message, variant }) {
      if (this.alertShow) return

      this.alertVariant = variant
      this.alertMessage = message
      this.alertShow = true
      setTimeout(() => this.alertShow = false, 2000)
    }
  }
}
</script>

<style scoped>
.email-animation-enter-active,
.email-animation-leave-active {
  transition: all 0.3s ease-in-out;
  position: relative;
}

.email-animation-enter {
  opacity: 0;
  transform: translateX(-100%);
}

.email-animation-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.email-animation-leave {
  opacity: 1;
  transform: translateX(0);
}

.email-animation-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
